import { useEffect, useMemo, useState } from 'react';
import { Lock } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import { useRBAC } from '../../../contexts/RBACContext';
import { LoadingSpinner } from '../../../shared/components/LoadingSpinner';
import {
  fetchBpTickets,
  updateBpTicketAssignee,
  updateBpTicketCategory,
  updateBpTicketPriority,
  updateBpTicketStatus,
} from '../services/bpTicketsApi';
import type { BpTicket, TicketCategory, TicketPriority, TicketStatus } from '../types/bpTickets';
import { TICKET_STATUSES } from '../types/bpTickets';
import { CategoryBadge, PriorityBadge } from '../components/ticketing/badges';
import { daysAgo, nameFromEmail } from '../components/ticketing/format';
import { MultiSelect } from '../components/ticketing/MultiSelect';
import { StatusSelect } from '../components/ticketing/StatusSelect';
import { TicketDetailModal } from '../components/ticketing/TicketDetailModal';
import type { AssigneeOption } from '../components/ticketing/AssigneeSelect';
import { usePeopleTicketsAccess } from '../components/peopleTickets/usePeopleTicketsAccess';
import { PeopleTicketManagement } from './peopleTickets/PeopleTicketManagement';

const TICKET_MANAGEMENT_PERMISSION = 'space.people.ticket_management.view';
const DEFAULT_STATUSES: TicketStatus[] = ['Submitted', 'In Progress', 'Waiting for Employee'];
const STATUS_ORDER: TicketStatus[] = TICKET_STATUSES;

/** Urgent first, then status stage, then longest-waiting (oldest createdAt) first. */
function compareTickets(a: BpTicket, b: BpTicket): number {
  if (a.priority !== b.priority) return a.priority === 'Urgent' ? -1 : 1;
  const statusDiff = STATUS_ORDER.indexOf(a.status) - STATUS_ORDER.indexOf(b.status);
  if (statusDiff !== 0) return statusDiff;
  const aTime = a.createdAt ? new Date(a.createdAt).getTime() : 0;
  const bTime = b.createdAt ? new Date(b.createdAt).getTime() : 0;
  return aTime - bTime;
}

function AccessRestricted() {
  return (
    <div className="flex min-h-[60vh] items-center justify-center p-6">
      <div className="w-full max-w-sm space-y-4 rounded-xl border border-zinc-200 bg-white p-8 text-center shadow-sm">
        <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-zinc-100">
          <Lock className="h-6 w-6 text-zinc-400" />
        </div>
        <div>
          <h2 className="text-lg font-semibold text-zinc-900">Access restricted</h2>
          <p className="mt-1 text-sm text-zinc-500">
            Ticket Management shows every employee's ticket. Access is limited to the{' '}
            <span className="font-medium">ticket-management-viewers</span> group. If you need access, ask a
            People Space admin.
          </p>
        </div>
      </div>
    </div>
  );
}

function TicketManagementContent() {
  const { user } = useAuth();
  const myEmail = (user?.email || '').toLowerCase();

  const [tickets, setTickets] = useState<BpTicket[]>([]);
  const [statusFilter, setStatusFilter] = useState<Set<string>>(new Set(DEFAULT_STATUSES));
  const [driFilter, setDriFilter] = useState<Set<string>>(new Set());
  const [openTicketId, setOpenTicketId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBpTickets()
      .then(setTickets)
      .finally(() => setLoading(false));
  }, []);

  const allDris = useMemo(
    () =>
      [...new Map(tickets.filter((t) => t.dri).map((t) => [t.dri!.email, t.dri!.name])).entries()].sort((a, b) =>
        a[1].localeCompare(b[1]),
      ),
    [tickets],
  );

  // Same list, shaped for AssigneeSelect (the ticket detail modal's reassignment dropdown) rather
  // than for MultiSelect's {value, label} filter options above.
  const assigneeOptions: AssigneeOption[] = useMemo(
    () => allDris.map(([email, name]) => ({ email, name })),
    [allDris],
  );

  const grouped = useMemo(() => {
    const filtered = tickets.filter(
      (t) =>
        (statusFilter.size === 0 || statusFilter.has(t.status)) &&
        (driFilter.size === 0 || (t.dri && driFilter.has(t.dri.email))),
    );
    const byDri = new Map<string, { name: string; tickets: BpTicket[] }>();
    for (const t of filtered) {
      if (!t.dri) continue;
      const bucket = byDri.get(t.dri.email) ?? { name: t.dri.name, tickets: [] };
      bucket.tickets.push(t);
      byDri.set(t.dri.email, bucket);
    }
    for (const bucket of byDri.values()) bucket.tickets.sort(compareTickets);
    const emails = [...byDri.keys()].sort((a, b) => byDri.get(a)!.name.localeCompare(byDri.get(b)!.name));
    // Your own Business Partner group always leads, rest stay alphabetical.
    emails.sort((a, b) => {
      if (a === myEmail) return -1;
      if (b === myEmail) return 1;
      return 0;
    });
    return emails.map((email) => [email, byDri.get(email)!] as const);
  }, [tickets, statusFilter, driFilter, myEmail]);

  async function handleStatusChange(ticket: BpTicket, status: TicketStatus) {
    setTickets((prev) => prev.map((t) => (t.id === ticket.id ? { ...t, status } : t)));
    await updateBpTicketStatus(ticket.id, status);
  }

  async function handleCategoryChange(ticket: BpTicket, category: TicketCategory) {
    setTickets((prev) => prev.map((t) => (t.id === ticket.id ? { ...t, category } : t)));
    await updateBpTicketCategory(ticket.id, category);
  }

  async function handlePriorityChange(ticket: BpTicket, priority: TicketPriority) {
    setTickets((prev) => prev.map((t) => (t.id === ticket.id ? { ...t, priority } : t)));
    await updateBpTicketPriority(ticket.id, priority);
  }

  // Reassignment moves the ticket to a different DRI bucket below — `grouped` re-derives from
  // `tickets`, so no separate bookkeeping is needed for that move.
  async function handleAssigneeChange(ticket: BpTicket, assignee: AssigneeOption) {
    setTickets((prev) => prev.map((t) => (t.id === ticket.id ? { ...t, dri: assignee } : t)));
    await updateBpTicketAssignee(ticket.id, assignee.email);
  }

  const openTicket = tickets.find((t) => t.id === openTicketId) ?? null;

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <LoadingSpinner size="lg" aria-label="Loading tickets" />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-5xl p-6">
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-xl font-semibold text-black">Ticket Overview</h1>
        <div className="flex gap-2">
          <MultiSelect
            label="Business Partner"
            options={allDris.map(([email, name]) => ({ value: email, label: name }))}
            selected={driFilter}
            onChange={setDriFilter}
          />
          <MultiSelect
            label="Status"
            options={TICKET_STATUSES.map((s) => ({ value: s, label: s }))}
            selected={statusFilter}
            onChange={setStatusFilter}
          />
        </div>
      </div>

      <div className="space-y-6">
        {grouped.map(([email, bucket]) => (
          <section key={email} className="rounded-lg border border-pearl bg-white">
            <header className="flex items-center justify-between border-b border-pearl bg-snow px-4 py-2.5">
              <h2 className="text-sm font-semibold text-black">
                {bucket.name}
                {email === myEmail && <span className="ml-1.5 text-xs font-regular text-trustedBlue">(you)</span>}
              </h2>
              <span className="text-xs text-iron">
                {bucket.tickets.length} ticket{bucket.tickets.length === 1 ? '' : 's'}
              </span>
            </header>
            <ul className="list-none divide-y divide-pearl">
              {bucket.tickets.map((t) => (
                <li
                  key={t.id}
                  className="flex cursor-pointer items-end justify-between gap-4 px-4 py-3 hover:bg-snow"
                  onClick={() => setOpenTicketId(t.id)}
                >
                  <div className="min-w-0 flex-1 space-y-1">
                    <div className="flex items-baseline gap-2">
                      <span className="shrink-0 text-xs text-iron">#{t.ticketNumber}</span>
                      <p className="min-w-0 truncate text-sm font-semibold text-black">{t.summary}</p>
                    </div>
                    <div className="flex flex-wrap items-center gap-2 text-xs text-iron">
                      <span>
                        {nameFromEmail(t.submittedBy)} · {t.department || '—'}
                      </span>
                      <CategoryBadge category={t.category} />
                      <span>Submitted {daysAgo(t.createdAt)}</span>
                    </div>
                  </div>
                  <div
                    className="flex shrink-0 flex-col items-end gap-1.5"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <PriorityBadge priority={t.priority} />
                    <StatusSelect status={t.status} onChange={(status) => handleStatusChange(t, status)} />
                  </div>
                </li>
              ))}
            </ul>
          </section>
        ))}
        {grouped.length === 0 && <p className="text-iron">No tickets match this filter.</p>}
      </div>

      {openTicket && (
        <TicketDetailModal
          ticket={openTicket}
          assigneeOptions={assigneeOptions}
          onClose={() => setOpenTicketId(null)}
          onStatusChange={(status) => handleStatusChange(openTicket, status)}
          onCategoryChange={(category) => handleCategoryChange(openTicket, category)}
          onPriorityChange={(priority) => handlePriorityChange(openTicket, priority)}
          onAssigneeChange={(assignee) => handleAssigneeChange(openTicket, assignee)}
        />
      )}
    </div>
  );
}

export default function TicketManagementView() {
  const { loading: rbacLoading, permissions, isCoreAdmin } = useRBAC();
  // The People Tickets pilot gate lives in the proxy; inside it, this page is the merged table.
  // Outside it (everyone until cutover) it stays the BP Tickets list below, unchanged.
  const { access, loading: accessLoading } = usePeopleTicketsAccess();
  const loading = rbacLoading || accessLoading;

  const allowed = isCoreAdmin || permissions.has('*') || permissions.has(TICKET_MANAGEMENT_PERMISSION);

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <LoadingSpinner size="lg" aria-label="Checking access" />
      </div>
    );
  }

  if (!allowed) return <AccessRestricted />;
  if (access?.enabled && access.canManage) return <PeopleTicketManagement access={access} />;

  return <TicketManagementContent />;
}
